import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Building2, Info } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { DataTable } from '../../../../components/ui/DataTable';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { Switch } from '../../../../components/ui/Switch';
import { useNotice } from '../../../../hooks/useNotice';
import {
  fetchWarehouseClients,
  updateWarehouseClient,
} from '../../../../features/warehouseClients/warehouseClientsThunks';
import {
  CLIENT_PREFERENCES,
  manageBlocker,
  preferenceDoneNotice,
  quotaLabel,
} from '../../../../features/warehouseClients/warehouseClientsUtils';
import {
  selectCurrentWarehouse,
  selectWarehouses,
} from '../../../../features/warehouseContext/warehouseContextSlice';

/**
 * `/warehouse/clients`: who keeps goods in this warehouse, and how each one
 * wants to work.
 *
 * Only the operating preferences are switched here. The module, the quota and
 * the term are commercial — NinjaHubs agrees them with the operator, not the
 * operator with itself — and are shown as facts. Who may switch is said by the
 * server on every row (`can_manage`): the rules of a warehouse are its
 * holder's, and a client sees its own row and nobody else's.
 */
export const WarehouseClients = () => {
  const dispatch = useDispatch();

  const { clients, loading, error, busyId } = useSelector((state) => state.warehouseClients);
  const warehouse = useSelector(selectCurrentWarehouse);
  const warehouses = useSelector(selectWarehouses);

  const setNotice = useNotice('clients-notice');

  // Fetch when the warehouse in use changes
  useEffect(() => {
    dispatch(fetchWarehouseClients());
  }, [dispatch, warehouse?.id]);


  const showWarehouse = !warehouse && warehouses.length > 1;

  const handleSwitch = async (row, key, value) => {
    const action = await dispatch(updateWarehouseClient({ contractId: row.id, key, value }));
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? preferenceDoneNotice(row, key, value)
        : { type: 'error', message: action.payload }
    );
  };

  const columns = [
    {
      header: 'CLIENTE',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-ninja-dark dark:text-white">{row.company.name}</span>
          <span className="text-xs text-text-muted font-mono mt-0.5">{row.company.rut}</span>
        </div>
      ),
    },
    ...(showWarehouse
      ? [
          {
            header: 'BODEGA',
            cell: (row) => <span className="text-text-secondary">{row.warehouse.name}</span>,
          },
        ]
      : []),
    {
      // The effective value, not the contract's raw one: an addon nobody paid
      // for is not operated, and showing it on would be a false promise.
      header: 'MÓDULO',
      cell: (row) => (
        <ToneBadge tone={row.module_active ? 'success' : 'neutral'} testId="client-module">
          {row.module_active ? 'Activo' : row.module_lite ? 'Lite' : 'Apagado'}
        </ToneBadge>
      ),
    },
    ...CLIENT_PREFERENCES.map((preference) => ({
      header: preference.label.toUpperCase(),
      cell: (row) => {
        const blocker = manageBlocker(row);
        const value = Boolean(row[preference.key]);
        return (
          <div className="flex flex-col gap-1" title={blocker || (value ? preference.on : preference.off)}>
            <Switch
              checked={value}
              onChange={(next) => handleSwitch(row, preference.key, next)}
              disabled={Boolean(blocker) || busyId === row.id}
              ariaLabel={`${preference.label} para ${row.company.name}`}
              testId={`client-${preference.key}`}
            />
            <span className="text-xs text-text-muted">
              {value ? preference.onShort : preference.offShort}
            </span>
          </div>
        );
      },
    })),
    {
      header: 'CUPO',
      cell: (row) => <span className="tabular-nums">{quotaLabel(row)}</span>,
    },
    {
      header: 'CONTRATO',
      cell: (row) => (
        <ToneBadge tone={row.is_active ? 'success' : 'danger'} testId="client-contract">
          {row.is_active ? 'Vigente' : 'Inactivo'}
        </ToneBadge>
      ),
    },
  ];

  const emptyMessage = warehouse
    ? `${warehouse.name} no tiene clientes dados de alta. El alta de un cliente la hace NinjaHubs.`
    : 'No hay clientes dados de alta en las bodegas de las que eres titular. El alta de un cliente la hace NinjaHubs.';

  return (
    <div data-testid="warehouse-clients-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Building2}
        title="Clientes"
        description="Las empresas que guardan mercadería acá y cómo trabaja cada una. El cupo y la activación del módulo son términos del contrato y se muestran como dato: se pactan fuera de esta pantalla."
      />

      {warehouse && !warehouse.can_manage && (
        <p
          data-testid="clients-readonly-note"
          className="flex items-center gap-2 text-sm text-text-secondary"
        >
          <Info size={16} className="text-info flex-shrink-0" />
          Guardas mercadería en {warehouse.name}, pero el espacio no es tuyo: ves cómo está
          configurada tu operación ahí y no la de otros.
        </p>
      )}

      <DataTable
        columns={columns}
        data={clients}
        totalItems={clients.length}
        currentPage={1}
        itemsPerPage={100}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchWarehouseClients())}
        itemLabel="clientes"
        emptyMessage={emptyMessage}
      />

      <section
        data-testid="clients-help"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border space-y-2 text-sm text-text-secondary"
      >
        {CLIENT_PREFERENCES.map((preference) => (
          <p key={preference.key}>
            <strong className="text-ninja-dark dark:text-white">{preference.label}</strong> encendido:{' '}
            {preference.on} Apagado: {preference.off}
          </p>
        ))}
      </section>

    </div>
  );
};
